import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BookHeart,
  Compass,
  HeartHandshake,
  Layers,
  LifeBuoy,
  Moon,
  Pause,
  PiggyBank,
  ShieldCheck,
  Sprout,
  Wind,
  Bookmark,
} from "lucide-react";
import PageContainer from "@/components/PageContainer";
import BrandHeader from "@/components/BrandHeader";
import { getDrafts, getProtectItems } from "@/lib/storage";

const MOODS = [
  { symbol: "☹", label: "Really tough" },
  { symbol: "⌢", label: "Tough" },
  { symbol: "—", label: "Okay" },
  { symbol: "⌣", label: "A little lighter" },
  { symbol: "☺", label: "Good" },
];

export default function Tools() {
  const nav = useNavigate();
  const [protectItems, setProtectItems] = useState([]);
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    setProtectItems(getProtectItems());
    setDrafts(getDrafts());
  }, []);

  const heldDraft = useMemo(
    () => drafts.find((draft) => draft.status === "held"),
    [drafts]
  );

  return (
    <PageContainer className="pt-7" testid="tools-page">
      <BrandHeader compact />

      <section className="relative mb-7 overflow-hidden">
        <div
          aria-hidden
          className="absolute -right-16 top-1 w-64 h-44 rounded-full opacity-70"
          style={{
            background:
              "radial-gradient(circle at 32% 40%, rgba(214,184,132,0.48), rgba(224,200,208,0.38) 45%, transparent 72%)",
          }}
        />
        <p
          className="relative text-[10px] uppercase tracking-[0.34em] mb-2"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Avenues
        </p>
        <h1
          className="relative text-[44px] leading-[0.98] tracking-[-0.03em]"
          style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
        >
          Ways forward
          <br />
          <em
            className="font-normal"
            style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}
          >
            from here.
          </em>
        </h1>
        <p
          className="relative mt-4 max-w-[360px] text-[15px] leading-relaxed"
          style={{ color: "var(--un-text-2)" }}
        >
          Choose an internal tool, a small real-world step, or support when that fits. Avenues are possibilities, not assignments.
        </p>
      </section>

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-[1.75rem] p-5 mb-4"
        style={{
          background: "rgba(253,250,246,0.88)",
          boxShadow: "0 12px 34px rgba(74,45,72,0.08)",
          border: "1px solid rgba(91,57,84,0.06)",
        }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Wind size={26} style={{ color: "var(--un-plum)" }} />
          <div className="flex-1">
            <h2
              className="text-[22px] leading-tight"
              style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
            >
              Immediate reset
            </h2>
            <p
              className="italic text-[15px]"
              style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}
            >
              Help for right now.
            </p>
          </div>
          <Link
            to="/reset"
            data-testid="tools-reset-link"
            className="px-4 py-2 rounded-full text-[12px] font-medium un-press"
            style={{ background: "rgba(224,200,208,0.36)", color: "var(--un-plum)" }}
          >
            Open
          </Link>
        </div>
        <p className="text-[13px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
          Anxious, angry, overwhelmed, an urge pushing at you — pick the moment
          and get a short guided reset, most in 1–5 minutes.
        </p>
        <button
          data-testid="tools-toolkit-link"
          onClick={() => nav("/toolkit")}
          className="mt-3 w-full rounded-2xl px-4 py-3 text-left flex items-center justify-between un-press"
          style={{ background: "rgba(224,200,208,0.25)", color: "var(--un-text-2)" }}
        >
          <span className="flex items-center gap-2">
            <Bookmark size={15} style={{ color: "var(--un-plum)" }} />
            Your saved toolkit — what works for you
          </span>
          <ArrowRight size={16} />
        </button>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-[1.75rem] p-5 mb-4"
        style={{
          background: "rgba(253,250,246,0.88)",
          boxShadow: "0 12px 34px rgba(74,45,72,0.08)",
          border: "1px solid rgba(91,57,84,0.06)",
        }}
      >
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck size={26} style={{ color: "var(--un-plum)" }} />
          <div className="flex-1">
            <h2
              className="text-[22px] leading-tight"
              style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
            >
              Protect list
            </h2>
            <p
              className="italic text-[15px]"
              style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}
            >
              Name what matters.
            </p>
          </div>
          <Link
            to="/protect"
            className="px-4 py-2 rounded-full text-[12px] font-medium un-press"
            style={{ background: "rgba(224,200,208,0.36)", color: "var(--un-plum)" }}
          >
            Manage
          </Link>
        </div>

        {protectItems.length ? (
          <div className="rounded-2xl px-4" style={{ background: "rgba(255,255,255,0.5)" }}>
            {protectItems.slice(0, 3).map((item, index) => (
              <div
                key={item.id}
                className="flex items-center gap-3 py-3"
                style={{ borderBottom: index < Math.min(protectItems.length, 3) - 1 ? "1px solid rgba(91,57,84,0.08)" : "none" }}
              >
                <span
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm"
                  style={{ background: index % 2 ? "#ead8df" : "#e5cfc7", color: "var(--un-plum-deep)" }}
                >
                  {item.label.slice(0, 1).toUpperCase()}
                </span>
                <span className="text-[15px]" style={{ color: "var(--un-text-1)" }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <button
            onClick={() => nav("/protect")}
            className="w-full rounded-2xl px-4 py-3 text-left flex items-center justify-between un-press"
            style={{ background: "rgba(224,200,208,0.25)", color: "var(--un-text-2)" }}
          >
            <span>Add someone or something you trust</span>
            <ArrowRight size={16} />
          </button>
        )}
      </motion.section>

      <button
        onClick={() => nav("/drafts")}
        className="w-full text-left rounded-[1.75rem] p-5 mb-4 un-press"
        style={{
          background: "linear-gradient(135deg, #4A2D48 0%, #6D4866 100%)",
          color: "#fffaf5",
          boxShadow: "0 14px 32px rgba(74,45,72,0.18)",
        }}
      >
        <div className="flex items-center gap-3">
          <Moon size={25} />
          <div className="flex-1">
            <p className="text-[21px]" style={{ fontFamily: "Lora, serif" }}>
              Drafts on hold
            </p>
            <p className="italic text-[15px] opacity-75" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Sleep on it safely.
            </p>
          </div>
          <ArrowRight size={18} className="opacity-75" />
        </div>
        {heldDraft && (
          <div className="mt-4 rounded-2xl p-4 text-[14px]" style={{ background: "rgba(255,250,245,0.93)", color: "var(--un-text-1)" }}>
            “{heldDraft.text.length > 70 ? `${heldDraft.text.slice(0, 70)}…` : heldDraft.text}”
          </div>
        )}
      </button>

      <section
        className="rounded-[1.75rem] p-5 mb-4"
        style={{
          background: "rgba(253,250,246,0.86)",
          boxShadow: "0 10px 28px rgba(74,45,72,0.07)",
          border: "1px solid rgba(91,57,84,0.06)",
        }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Sprout size={26} style={{ color: "var(--un-plum)" }} />
          <div>
            <h2 className="text-[22px]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>
              Check in
            </h2>
            <p className="italic text-[15px]" style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}>
              A kinder you, from here.
            </p>
          </div>
        </div>
        <p className="text-[14px] mb-3" style={{ color: "var(--un-text-2)" }}>
          How are you feeling right now?
        </p>
        <div className="grid grid-cols-5 gap-1.5">
          {MOODS.map((mood) => (
            <button
              key={mood.label}
              onClick={() => nav("/check-in")}
              aria-label={mood.label}
              className="py-2 rounded-2xl un-press"
              style={{ background: "rgba(224,200,208,0.32)", color: "var(--un-plum)" }}
            >
              <span className="block text-[20px] leading-none">{mood.symbol}</span>
              <span className="sr-only">{mood.label}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 mb-8">
        <ToolLink to="/safe-mode" icon={ShieldCheck} title="Safer Tonight" note="Make a plan for tonight" safe />
        <ToolLink to="/peace-compass" icon={Compass} title="Peace Compass" note="Choose a direction" />
        <ToolLink to="/zen" icon={Pause} title="Zen Mode" note="Pause first" />
        <ToolLink to="/mosaic" icon={Layers} title="Mosaic" note="Keep a piece" />
        <ToolLink to="/journal" icon={BookHeart} title="Journal" note="Write privately" />
        <ToolLink to="/safereach" icon={LifeBuoy} title="Safe Reach" note="Find support" safe />
        <ToolLink to="/future-funds" icon={PiggyBank} title="Look Forward" note="Make room for something" />
        <ToolLink to="/letters" icon={HeartHandshake} title="Tomorrow-you" note="Send hope ahead" />
      </div>
    </PageContainer>
  );
}

function ToolLink({ to, icon: Icon, title, note, safe = false, wide = false }) {
  return (
    <Link
      to={to}
      className={`${wide ? "col-span-2" : ""} rounded-3xl p-4 un-press`}
      style={{
        background: safe ? "rgba(74,107,93,0.11)" : "rgba(91,57,84,0.055)",
        color: safe ? "var(--un-safe)" : "var(--un-plum)",
      }}
    >
      <Icon size={18} />
      <p className="mt-2 text-[14px] font-medium" style={{ color: "var(--un-text-1)" }}>{title}</p>
      <p className="text-[11px]" style={{ color: "var(--un-text-3)" }}>{note}</p>
    </Link>
  );
}
